import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useStep } from "usehooks-ts";

import type { SetStateType } from "@/utils";
import { roundUpToQuarterHour } from "@/utils/date";
import { UppyContext } from "./UppyContextProvider";

export enum UploadType {
  File = "File",
  TTS = "TTS",
}

type ScheduledUploadDetails = {
  title: string;
  description?: string;
  internalName?: string;
};

const timezone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone;

type SchedulingState = {
  currentStep?: number;
  displayName?: string;
  doesRepeat?: boolean;
  unsavedChanges?: boolean;
  goToNextStep: () => void;
  goToPrevStep: () => void;
  repeatFrequency?: number;
  repeatPeriod?: number;
  resetAll: () => void;
  resetSteps: () => void;
  scheduledText?: string;
  selectedChannels?: Array<string>;
  selectedDaysOfWeek?: Array<number>;
  selectedDate?: Date;
  scheduledUploadDetails?: ScheduledUploadDetails;
  setDisplayName: SetStateType<string>;
  setDoesRepeat?: SetStateType<boolean>;
  setRepeatFrequency: SetStateType<number>;
  setRepeatPeriod: SetStateType<number>;
  setSelectedChannels: SetStateType<Array<string>>;
  setSelectedDaysOfWeek: SetStateType<Array<number>>;
  setSelectedDate: SetStateType<Date>;
  setScheduledText: SetStateType<string>;
  setUploadDetails: SetStateType<ScheduledUploadDetails>;
  setUploadType: SetStateType<UploadType>;
  timezone?: string;
  uploadDetails?: ScheduledUploadDetails;
  uploadType?: UploadType;
};

export const SchedulingContext = createContext<SchedulingState>({
  setDisplayName: () => {},
  setRepeatFrequency: () => {},
  setRepeatPeriod: () => {},
  setSelectedChannels: () => {},
  setSelectedDaysOfWeek: () => {},
  setSelectedDate: () => {},
  setScheduledText: () => {},
  setUploadDetails: () => {},
  setUploadType: () => {},
  goToNextStep: () => {},
  goToPrevStep: () => {},
  resetAll: () => {},
  resetSteps: () => {},
  timezone,
});

const SchedulingContextProvider = ({ children }) => {
  const { addedFile, removeFiles } = useContext(UppyContext);
  const [uploadDetails, setUploadDetails] = useState<ScheduledUploadDetails>({
    title: "",
    description: "",
  });
  const [scheduledText, setScheduledText] = useState<string>("");
  const [displayName, setDisplayName] = useState<string>("");
  const [uploadType, setUploadType] = useState<UploadType>(UploadType.File);
  const [selectedChannels, setSelectedChannels] = useState<Array<string>>([]);
  // default date to the nearest quarter of the hour based off Date.now()
  const [selectedDate, setSelectedDate] = useState<Date>(roundUpToQuarterHour(new Date(Date.now())));
  const [repeatFrequency, setRepeatFrequency] = useState<number>(1);
  const [repeatPeriod, setRepeatPeriod] = useState<number>(1);
  const [doesRepeat, setDoesRepeat] = useState<boolean>(false);
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<Array<number>>([]);

  useEffect(() => {
    if (uploadType === "TTS" && displayName?.length > 0) {
      setDisplayName(() => "");
    }
  }, [uploadType]);

  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
  // max steps of 3
  const [currentStep, helpers] = useStep(3);

  const { goToNextStep, goToPrevStep, reset, setStep } = helpers;

  const resetSteps = useCallback(() => {
    reset();
  }, [reset]);

  const resetAll = useCallback(() => {
    setUploadType(UploadType.File);
    setUploadDetails({
      title: "",
      description: "",
    });
    setScheduledText("");
    setDisplayName("");
    setSelectedChannels([]);
    setSelectedDate(roundUpToQuarterHour(new Date(Date.now())));
    setRepeatFrequency(1);
    setRepeatPeriod(1);
    setDoesRepeat(false);
    setSelectedDaysOfWeek([]);
    removeFiles();
    resetSteps();
  }, [removeFiles, resetSteps]);

  useEffect(() => {
    if (scheduledText || displayName || addedFile) {
      setUnsavedChanges(true);
    } else {
      setUnsavedChanges(false);
    }
  }, [addedFile, scheduledText, displayName]);

  const schedulingState = useMemo<SchedulingState>(
    () => ({
      currentStep,
      displayName,
      doesRepeat,
      unsavedChanges,
      uploadDetails,
      uploadType,
      repeatFrequency,
      repeatPeriod,
      scheduledText,
      selectedChannels,
      selectedDaysOfWeek,
      selectedDate,
      setDisplayName,
      setDoesRepeat,
      setRepeatFrequency,
      setRepeatPeriod,
      setSelectedChannels,
      setSelectedDaysOfWeek,
      setSelectedDate,
      setScheduledText,
      setUploadDetails,
      setUploadType,
      timezone,
      goToNextStep,
      goToPrevStep,
      resetAll,
      resetSteps,
    }),
    [
      currentStep,
      displayName,
      doesRepeat,
      unsavedChanges,
      uploadDetails,
      uploadType,
      repeatFrequency,
      repeatPeriod,
      scheduledText,
      selectedChannels,
      selectedDaysOfWeek,
      selectedDate,
      goToNextStep,
      goToPrevStep,
      resetAll,
      resetSteps,
    ],
  );
  return <SchedulingContext.Provider value={schedulingState}>{children}</SchedulingContext.Provider>;
};

export default SchedulingContextProvider;
